import React, { Fragment, useMemo, useCallback } from 'react';
import filter from 'lodash/filter';

import {
  IsFetched,
  ErrorMessage,
  FetchItemsLoadMoreItems
} from '../../../../../types';
import { TeamNanoID } from '../../../../teams/teamsTypes';

import {
  LifestyleNanoID,
  FetchLifestylesFilters,
  ChangeLifestylesFiltersFunc,
  FetchLifestylesLimit,
  FetchLifestylesPage,
  UpdateLifestyleCacheAction
} from '../../../../lifestyles/lifestylesTypes';
import {
  ThreeDStockLifestylesCompactListLifestyle,
  ThreeDStockLifestylesCompactListLifestyles
} from './ThreeDStockLifestylesCompactList.types';

import { ThreeDStockLifestylesCompactListItem } from '../ThreeDStockLifestylesCompactListItem';
import { ThreeDStockLifestylesListLightboxTitle } from '../ThreeDStockLifestylesList/components/ThreeDStockLifestylesListLightboxTitle';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { Loading } from '../../../../../helpers/Loading';
import { NoResults } from '../../../../../helpers/NoResults';
import {
  LightboxWrapper,
  useLightboxWrapper,
  LightboxRenderImageTitle
} from '../../../../../helpers/LightboxWrapper';
import { LoadMoreButtonHelper } from '../../../../../helpers/buttons/LoadMoreButtonHelper';
import { LoadingSkeleton } from '../../../../../helpers/LoadingSkeleton';

import { Files } from '../../../../../utils/Files';

import { LifestylesPermissions } from '../../../../lifestyles/lifestylesConstants';

type ThreeDStockLifestylesCompactListProps = {
  onLifestyleEditButtonMouseEnter?: (lifestyleNanoId: LifestyleNanoID) => void;
  lifestyles: ThreeDStockLifestylesCompactListLifestyles;
  lifestylesError: ErrorMessage;
  lifestylesFetched: IsFetched;
  lifestylesIsPlaceholderData: boolean;
  lifestylesTotalCount: number;
  lifestylesFilters: FetchLifestylesFilters;
  changeLifestylesFilters: ChangeLifestylesFiltersFunc;
  hasNextLifestylesPage: boolean;
  lifestylesPage: FetchLifestylesPage;
  lifestylesLimit: FetchLifestylesLimit;
  loadMoreLifestyles: FetchItemsLoadMoreItems;
  lifestylesFetchingNextPage: boolean;
  updateLifestyleCache?: UpdateLifestyleCacheAction<ThreeDStockLifestylesCompactListLifestyle>;
  companyNanoId?: TeamNanoID;
  isMyLibrary?: boolean;
};

function ThreeDStockLifestylesCompactList({
  onLifestyleEditButtonMouseEnter,
  lifestyles,
  lifestylesError,
  lifestylesFetched,
  lifestylesIsPlaceholderData,
  lifestylesTotalCount,
  lifestylesFilters,
  changeLifestylesFilters,
  hasNextLifestylesPage,
  lifestylesPage,
  lifestylesLimit,
  loadMoreLifestyles,
  lifestylesFetchingNextPage,
  updateLifestyleCache,
  companyNanoId,
  isMyLibrary
}: ThreeDStockLifestylesCompactListProps) {
  const lifestylesInLightbox =
    useMemo<ThreeDStockLifestylesCompactListLifestyles>(() => {
      return filter<ThreeDStockLifestylesCompactListLifestyle>(
        lifestyles,
        (lifestyle) =>
          lifestyle.image?.file && Files.isImage(lifestyle.image.file)
      );
    }, [lifestyles]);

  const {
    handleLightboxClose,
    handleLightboxNext,
    handleLightboxOpen,
    handleLightboxOpenOnSlide,
    handleLightboxPrev,
    index,
    imagesCount,
    imageItem,
    lightBoxOpened,
    mainSrc,
    prevSrc,
    nextSrc
  } = useLightboxWrapper({
    items: lifestylesInLightbox
  });

  const renderImageTitle = useCallback<LightboxRenderImageTitle>(
    ({ index }) => {
      const lifestyle = lifestylesInLightbox[index];
      if (!lifestyle) {
        return null;
      }

      return <ThreeDStockLifestylesListLightboxTitle lifestyle={lifestyle} />;
    },
    [lifestylesInLightbox]
  );

  return (
    <Fragment>
      <AlertMessage addClassName="m-4" message={lifestylesError} />
      <LoadingSkeleton
        loaded={lifestylesIsPlaceholderData || lifestylesFetched}
      >
        {lifestylesTotalCount === 0 && <NoResults addErrorClassName="m-4" />}
        {lifestylesTotalCount > 0 && (
          <div className="min-w-0 -mx-2 space-y-1">
            <div className="flex justify-center items-center">
              <Loading loaded={!lifestylesIsPlaceholderData} />
            </div>

            {lifestyles.map((lifestyle) => (
              <ThreeDStockLifestylesCompactListItem
                key={lifestyle.uuid}
                lifestyle={lifestyle}
                onLifestyleEditButtonMouseEnter={
                  onLifestyleEditButtonMouseEnter
                }
                onLightboxOpen={handleLightboxOpenOnSlide}
                updateLifestyleCache={updateLifestyleCache}
                companyNanoId={companyNanoId}
                isMyLibrary={isMyLibrary}
              />
            ))}

            {hasNextLifestylesPage && (
              <LoadMoreButtonHelper
                action={
                  LifestylesPermissions.READ_LIFESTYLES_LOAD_MORE_BUTTON_ITEMS_COUNT
                }
                dataGa="lifestyles-load-more-button"
                itemsTotalCount={lifestylesTotalCount}
                page={lifestylesPage}
                limit={lifestylesLimit}
                loadMoreItems={loadMoreLifestyles}
                itemsFetchingNextPage={lifestylesFetchingNextPage}
              />
            )}
          </div>
        )}
      </LoadingSkeleton>
      <LightboxWrapper
        handleLightboxClose={handleLightboxClose}
        handleLightboxNext={handleLightboxNext}
        handleLightboxOpen={handleLightboxOpen}
        handleLightboxPrev={handleLightboxPrev}
        index={index}
        imagesCount={imagesCount}
        imageItem={imageItem}
        lightBoxOpened={lightBoxOpened}
        mainSrc={mainSrc}
        nextSrc={nextSrc}
        prevSrc={prevSrc}
        renderImageTitle={renderImageTitle}
        // renderCustomButtons={renderLightboxButtons}
        withFullScreenButton
      />
    </Fragment>
  );
}

export default ThreeDStockLifestylesCompactList;
